import {
  CalculationDetailResponse,
  CalculationMode,
  CalculationRequest,
  CalculationType,
  CalculationUpdateRequest,
  CalculationWithOverviewResponseCollectionResponse,
  CalculationWithRelatedEntitiesResponseCollectionResponse,
  Condition,
  CustomExportRunResponse,
  LogicalOperator,
  Operation,
} from '@ydistri/api-sdk';
import { validationProblemsTag } from '../../apis/apiValidationProblems';
import { apiSlice, errorTransform, ErrorType } from '../../apis/api';
import { CalculationsCollection, TemplatesCollection } from '../../swagger/collections';
import {
  CalculationTableParams,
  PrivateFilterType,
  SignalCalculationStatusChanged,
} from './calculationsTypes';
import { signalrClient } from '../../signalr/client';
import {
  isCalculationCreated,
  isCalculationDeleted,
  isCalculationFinished,
  isCalculationInTemporaryStatus,
} from './calculationsLib';
import { apiCalculationDetail } from '../CalculationDetail/apiCalculationDetail';
import {
  ApiParams,
  forceRefetchForInfiniteScroll,
  OtherArgs,
  serializeFullApiBasedQueryArgs,
} from '@ydistri/ds';
import { constructRequestParams, getTags } from '../../apis/apiLib';
import { createDebugLog } from '@ydistri/utils';
import {
  SignalConfigurationChanged,
  SignalCustomExportRunStatusChanged,
} from '../../signalr/signalrInterfaces';
import { removeSelectedRowKey } from './calculationsSlice';
import { apiCalculationsCategories } from './apiCalculationsCategories';
import { apiCategoryInsights } from '../../components/global/CategoryInsights/apiCategoryInsights';
import { apiCalculationStatistics } from '../CalculationDetail/Statistics/apiCalculationStatistics';
import { ReduxState } from '../../store';
import { getSelectedProjectShortName } from '../../apis/apiHeader';

const debugLog = createDebugLog('Calculations:api');

const { TAGS, TAGS_ARRAY } = getTags('calculations');
const { TAGS: EXCLUSION_TAGS, TAGS_ARRAY: EXCLUSION_TAGS_ARRAY } =
  getTags('storeScheduleExclusions');

interface LoadCalculationToTemplateParams {
  fromCalculationId: number;
  toTemplateId: number;
}

export interface PatchCalculationPayload extends ApiParams {
  calculationId: number;
  data: CalculationUpdateRequest;
}

const getLatestCustomExportRun = async (
  calculationId: number,
  params: ApiParams,
): Promise<CustomExportRunResponse | null> => {
  const response = await CalculationsCollection.getCustomExportRuns(
    calculationId,
    {
      top: 1,
      conditions: [{ fieldName: 'IsLast', operation: Operation.Eq, value: true }],
    },
    constructRequestParams(params),
  );

  return response.data.data[0] ?? null;
};

const getConditions = (
  params: CalculationTableParams,
  calculationOwnerId?: string,
): Condition[] => {
  const conditions: Condition[] = [];

  if (params.productionCalculationsOnly) {
    conditions.push({
      fieldName: 'Type',
      operation: Operation.Eq,
      value: 'Production',
      logicalOperator: LogicalOperator.And,
    });
    if (params.showOwnersCalculationsOnly) {
      conditions.push({
        fieldName: 'IdentityUserId',
        operation: Operation.Eq,
        value: calculationOwnerId,
        logicalOperator: LogicalOperator.And,
      });
    }
  } else if (params.showOwnersCalculationsOnly) {
    conditions.push({
      fieldName: 'IdentityUserId',
      operation: Operation.Eq,
      value: calculationOwnerId,
      logicalOperator: LogicalOperator.And,
    });
    if (params.includePrivateCalculations === PrivateFilterType.NONE) {
      conditions.push({
        fieldName: 'Type',
        operation: Operation.Ne,
        value: 'Private',
        logicalOperator: LogicalOperator.And,
      });
    }
  } else {
    if (params.includePrivateCalculations === PrivateFilterType.NONE) {
      conditions.push({
        fieldName: 'Type',
        operation: Operation.Ne,
        value: 'Private',
        logicalOperator: LogicalOperator.And,
      });
    } else if (params.includePrivateCalculations === PrivateFilterType.MINE) {
      conditions.push({
        fieldName: 'Type',
        operation: Operation.Ne,
        value: 'Private',
        logicalOperator: LogicalOperator.Or,
      });
      conditions.push({
        fieldName: 'IdentityUserId',
        operation: Operation.Eq,
        value: calculationOwnerId,
        logicalOperator: LogicalOperator.Or,
      });
    }
  }
  return conditions;
};

export const apiCalculations = apiSlice
  .enhanceEndpoints({ addTagTypes: [...TAGS_ARRAY, ...EXCLUSION_TAGS_ARRAY] })
  .injectEndpoints({
    endpoints: builder => ({
      getCalculations: builder.query<
        CalculationWithRelatedEntitiesResponseCollectionResponse,
        CalculationTableParams
      >({
        queryFn: async args => {
          debugLog('getCalculations API starting, queryArgs: ', args);
          const query = {
            top: args.top,
            skip: args.skip,
            inlineCount: true,
            sortings: args.sortings,
            conditions: args.conditions,
            search: args.search,
          };

          const conditions = getConditions(args, args.calculationOwner);
          if (conditions.length > 0) {
            if (query.conditions) {
              query.conditions = [...query.conditions, ...conditions];
            } else {
              query.conditions = conditions;
            }
          }

          debugLog('getCalculations: calling API with query ', query);
          const response = await CalculationsCollection.getCalculations(
            query,
            constructRequestParams(args),
          );
          debugLog('getCalculations: response received');
          return { data: response.data };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.calculations, id: arg.projectShortName },
        ],
        async onCacheEntryAdded(arg, api) {
          try {
            await api.cacheDataLoaded;

            const listener = (signal: SignalCalculationStatusChanged) => {
              // eslint-disable-next-line no-console -- we want the output here
              console.log('SIGNAL SignalCalculationStatusChanged', signal);
              //if a new calculation is created or a calculation is deleted, reload the whole list
              //as it may affect the pagination
              if (isCalculationCreated(signal.status)) {
                api.dispatch(
                  apiCalculations.util.invalidateTags([
                    { type: TAGS.calculations, id: signal.projectSlug },
                  ]),
                );
                return;
              }

              if (isCalculationDeleted(signal.status)) {
                api.dispatch(removeSelectedRowKey({ tableId: 'all', data: signal.calculationId }));
                debugLog('Signalr: Calculation id: %d deleted', signal.calculationId);
                api.updateCachedData(draft => {
                  debugLog('Updating cached data...', signal.calculationId);
                  const indexToDelete = draft.data.findIndex(
                    calculation => calculation.id === signal.calculationId,
                  );
                  if (indexToDelete >= 0) {
                    debugLog('Deleting calculation from cache', signal.calculationId);
                    draft.data.splice(indexToDelete, 1);
                  } else {
                    debugLog('Calculation not found in cache, good', signal.calculationId);
                  }
                });
                return;
              }

              //if a calculation is queued or pending we can safely update the status in cache as no
              //function depends on queued or pending calculations
              if (isCalculationInTemporaryStatus(signal.status)) {
                api.updateCachedData(draft => {
                  const calculation = draft.data.find(
                    calculation => calculation.id === signal.calculationId,
                  );
                  if (calculation) {
                    calculation.status = signal.status;
                  } else {
                    //when computing merged calculations, submerged calculation status can change as well
                    //in that case, we need to find it in the list of all calculations and update its status
                    draft.data.find(
                      calculation =>
                        calculation.mode === CalculationMode.Merged &&
                        (calculation.subordinateCalculations ?? []).find(sc => {
                          if (sc.id === signal.calculationId) {
                            sc.status = signal.status;
                          }
                          return sc.id === signal.calculationId;
                        }),
                    );
                  }
                });
                return;
              }

              //if a calculation is completed or crashed we need to reload the calculation from the server
              //as there are functions that depend on the calculation being in final state
              if (isCalculationFinished(signal.status)) {
                //First find out if the calculation is in cache, so we do not call the backend unnecessarily.
                //We can't call backend from within updateCacheData as the callback cannot perform asynchronous operations
                //because the draft proxy will be gone by the time the backend call resolves.
                const cacheEntry = api.getCacheEntry();
                if (cacheEntry.data) {
                  const calculationIndex = cacheEntry.data.data.findIndex(
                    calculation =>
                      calculation.id === signal.calculationId ||
                      (calculation.mode === CalculationMode.Merged &&
                        (calculation.subordinateCalculations ?? []).find(
                          sub => sub.id === signal.calculationId,
                        )),
                  );
                  if (calculationIndex !== -1) {
                    // it can be different than the one in signal in case of submerged calculation
                    const calculationIdToReload = cacheEntry.data.data[calculationIndex].id;

                    //calculation is in cache, reload it from the backend
                    CalculationsCollection.getCalculations({
                      conditions: [
                        { fieldName: 'Id', operation: Operation.Eq, value: calculationIdToReload },
                      ],
                    }).then(response => {
                      const data = response.data.data;
                      if (data.length > 0) {
                        api.updateCachedData(draft => {
                          draft.data[calculationIndex] = data[0];
                        });
                      }
                    });
                  }
                }

                // TODO: add calculation IDs to the endpoints so it doesn't invalidate all calculations, but only finished ones
                api.dispatch(
                  apiCalculationsCategories.util.invalidateTags(
                    getTags('calculationCategories').TAGS_ARRAY,
                  ),
                );
                api.dispatch(
                  apiCategoryInsights.util.invalidateTags(getTags('categoryInsights').TAGS_ARRAY),
                );
                api.dispatch(
                  apiCalculationStatistics.util.invalidateTags(
                    getTags('calculationStatistics2').TAGS_ARRAY,
                  ),
                );
              }
            };

            //TODO - currently, we get calculation updates in TemplateConfigurationChanged endpoint, what is a bit weird
            const listener2 = (signal: SignalConfigurationChanged) => {
              // eslint-disable-next-line no-console -- we want the output here
              console.log('SIGNAL! SignalConfigurationChanged', signal);
              api.updateCachedData(draft => {
                //if it is change in normal calculation, we select it right away
                //if it is change in submerged calculation, we select its parent first
                const correctOrParentCalculation = draft.data.find(
                  calculation =>
                    calculation.id === signal.templateId ||
                    (calculation.subordinateCalculations ?? []).find(
                      c => c.id === signal.templateId,
                    ),
                );

                if (correctOrParentCalculation) {
                  //if id is not the same as the one from signal, it is submerged calculation - in that case, we find correct submerged "calculation"
                  const calculation =
                    correctOrParentCalculation.id !== signal.templateId
                      ? (correctOrParentCalculation.subordinateCalculations ?? []).find(
                          c => c.id === signal.templateId,
                        )
                      : correctOrParentCalculation;

                  if (!calculation) {
                    throw new Error(
                      "Didn't find correct submerged calculation, this should not happen!",
                    );
                  }

                  signal.updates.forEach(u => {
                    if (u.value) {
                      switch (u.fieldName) {
                        case 'name':
                          calculation.title = u.value;
                          break;
                        case 'description':
                          calculation.description = u.value;
                          break;
                        case 'isPap':
                          calculation.isPap = u.value === '1';
                          break;
                        case 'calculationTypeId':
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
                          calculation.type = u.value as CalculationType;

                          if (calculation.type === CalculationType.Production) {
                            api.dispatch(
                              removeSelectedRowKey({ tableId: 'all', data: calculation.id }),
                            );
                          }

                          break;
                      }
                    }
                  });
                }
              });
            };

            const latestCustomExportRunListener = (signal: SignalCustomExportRunStatusChanged) => {
              if (arg.projectShortName && signal.projectSlug !== arg.projectShortName) {
                return;
              }

              let shouldReloadLatestCustomExportRun = false;

              api.updateCachedData(draft => {
                const calculation = draft.data.find(
                  calculation => calculation.id === signal.calculationId,
                );

                if (!calculation) {
                  return;
                }

                if (calculation.latestCustomExportRun?.id === signal.customExportRunId) {
                  calculation.latestCustomExportRun.status = signal.status;
                  return;
                }

                shouldReloadLatestCustomExportRun = true;
              });

              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- updated synchronously inside updateCachedData callback
              if (!shouldReloadLatestCustomExportRun) {
                return;
              }

              getLatestCustomExportRun(signal.calculationId, arg)
                .then(latestCustomExportRun => {
                  api.updateCachedData(draft => {
                    const calculation = draft.data.find(
                      calculation => calculation.id === signal.calculationId,
                    );

                    if (calculation) {
                      calculation.latestCustomExportRun = latestCustomExportRun;
                    }
                  });
                })
                .catch((error: unknown) => {
                  debugLog(
                    'Signalr: Failed to reload latest custom export run for calculation id: %d',
                    signal.calculationId,
                    error,
                  );
                });
            };

            signalrClient.on('CalculationStatusChanged', listener);
            signalrClient.on('TemplateConfigurationChanged', listener2);
            signalrClient.on('CustomExportRunStatusChanged', latestCustomExportRunListener);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('CalculationStatusChanged', listener);
              signalrClient.off('TemplateConfigurationChanged', listener2);
              signalrClient.off('CustomExportRunStatusChanged', latestCustomExportRunListener);
            });

            await api.cacheEntryRemoved;
          } catch (error) {
            // eslint-disable-next-line no-console -- we want the output here
            console.log(error);
          }
        },
        serializeQueryArgs: serializeFullApiBasedQueryArgs<CalculationTableParams>(
          getSelectedProjectShortName,
        ),
        merge: (
          currentCache: CalculationWithOverviewResponseCollectionResponse,
          newItems: CalculationWithOverviewResponseCollectionResponse,
          otherArgs: OtherArgs<CalculationTableParams>,
        ) => {
          if (otherArgs.arg.skip === 0) {
            currentCache.data.splice(0, currentCache.data.length);
          }
          currentCache.data.push(...newItems.data);
          currentCache.totalCount = newItems.totalCount;
        },
        forceRefetch: forceRefetchForInfiniteScroll<CalculationTableParams | undefined>(),
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response here
      createCalculation: builder.mutation<void, CalculationRequest>({
        queryFn: async request => {
          try {
            const result = await CalculationsCollection.calculationsCreate(request);
            return { data: result.data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: errorTransform(err) };
          }
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response here
      runManualCalculation: builder.mutation<void, number>({
        queryFn: async calculationId => {
          try {
            await CalculationsCollection.patchCalculationPairings(calculationId);
            return { data: undefined };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: errorTransform(err) };
          }
        },
      }),

      deleteCalculation: builder.mutation<number, number>({
        queryFn: async (calculationId: number) => {
          try {
            await CalculationsCollection.deleteCalculation(calculationId);
            return { data: calculationId };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: errorTransform(err) };
          }
        },
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response here
      loadCalculationToTemplate: builder.mutation<void, LoadCalculationToTemplateParams>({
        queryFn: async ({ fromCalculationId, toTemplateId }) => {
          try {
            const { data } = await TemplatesCollection.loadFromCalculationTemplateConfiguration(
              toTemplateId,
              fromCalculationId,
            );

            return { data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        // The whole configuration changed, so every report of the destination is stale. The
        // opt-outs are replaced too; SignalR carries that to other sessions, this makes it
        // immediate for the one loading, and still works when the connection is down.
        invalidatesTags: (result, error, arg) => [
          validationProblemsTag({ type: 'Template', id: arg.toTemplateId }),
          EXCLUSION_TAGS.storeScheduleExclusions,
        ],
      }),

      patchCalculation: builder.mutation<CalculationDetailResponse, PatchCalculationPayload>({
        queryFn: async ({ calculationId, data }) => {
          const response = await CalculationsCollection.patchCalculation(calculationId, data);
          return { data: response.data.data };
        },
        onQueryStarted(payload, api) {
          const patchResult = api.dispatch(
            apiCalculationDetail.util.updateQueryData(
              'getCalculation',
              payload.calculationId,
              draft => {
                if (payload.data.calculationType) {
                  draft.type = payload.data.calculationType;
                } else {
                  Object.assign(draft, payload.data);
                }
              },
            ),
          );

          //update the cache for both the list and single calculation so it is
          //reflected in UI
          api.queryFulfilled
            .then(({ data }) => {
              api.dispatch(
                apiCalculationDetail.util.updateQueryData('getCalculation', data.id, draft => {
                  draft.isDeletable = data.isDeletable;
                }),
              );

              //iterate through all cached getCalculations queries and update the calculation
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the state is ReduxState
              const state = api.getState() as ReduxState;
              const queryKeys = apiCalculations.util.selectCachedArgsForQuery(
                state,
                'getCalculations',
              );

              queryKeys.forEach(qk => {
                api.dispatch(
                  apiCalculations.util.updateQueryData('getCalculations', qk, oldData => {
                    const calculationIndex = oldData.data.findIndex(
                      calculation => calculation.id === data.id,
                    );

                    //update the calculation in the redux store
                    //the returned object from edit is different type than the one in the redux store,
                    //so we need to update the relevant properties only

                    //we update only those properties that were changed through the patch payload

                    if (calculationIndex !== -1) {
                      const calculation = oldData.data[calculationIndex];

                      if (payload.data.calculationType) {
                        calculation.type = data.type;
                        calculation.isDeletable = data.isDeletable;
                      }
                      if (payload.data.isPap) {
                        calculation.isPap = data.isPap;
                      }

                      if (payload.data.title) {
                        calculation.title = data.title;
                      }

                      if (payload.data.description !== undefined) {
                        calculation.description = data.description;
                      }
                    }
                  }),
                );
              });
            })
            .catch(patchResult.undo);
        },
      }),
    }),
  });

export const {
  useGetCalculationsQuery,
  useCreateCalculationMutation,
  useDeleteCalculationMutation,
  useLoadCalculationToTemplateMutation,
  usePatchCalculationMutation,
  useRunManualCalculationMutation,
  useLazyGetCalculationsQuery,
} = apiCalculations;
